import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BatteryCharging, Gauge, Users, ArrowUpRight } from "lucide-react";
import { SectionHeading } from "../Reveal";
import { useLead } from "../../context/LeadContext";
import { formatCHF } from "../../lib/format";

const FILTERS = [
  { key: "alle", label: "Alle" },
  { key: "Elektro", label: "Elektro" },
  { key: "Plug-in Hybrid", label: "Plug-in Hybrid" },
  { key: "Hybrid", label: "Hybrid" },
  { key: "Benzin", label: "Benzin" },
  { key: "SUV", label: "SUV" },
  { key: "Kompakt", label: "Kompakt" },
  { key: "Nutzfahrzeug", label: "Nutzfahrzeuge" },
];

function matches(m, filter) {
  if (filter === "alle") return true;
  if (["Elektro", "Plug-in Hybrid", "Hybrid", "Benzin"].includes(filter)) return m.drivetrains.includes(filter);
  if (filter === "SUV") return m.category.includes("SUV");
  if (filter === "Kompakt") return m.category.includes("Kompakt") || m.category === "Kleinwagen";
  if (filter === "Nutzfahrzeug") return m.category === "Nutzfahrzeug";
  return true;
}

function ModelCard({ m, onShowDetail }) {
  const { requestOfferte } = useLead();
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      data-testid={`model-card-${m.id}`}
      className="group bg-white border border-neutral-200 overflow-hidden flex flex-col"
    >
      <button
        data-testid={`model-card-image-${m.id}`}
        onClick={() => onShowDetail(m)}
        className="relative aspect-[4/3] overflow-hidden bg-neutral-100 block w-full"
      >
        <img
          src={m.image_url}
          alt={m.name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <div className="absolute top-4 left-4 flex flex-wrap gap-2">
          {m.drivetrains.slice(0, 2).map((d) => (
            <span key={d} className="bg-neutral-950/85 backdrop-blur text-white text-[10px] uppercase tracking-[0.15em] font-semibold px-3 py-1.5">
              {d}
            </span>
          ))}
        </div>
      </button>
      <div className="p-6 flex flex-col flex-1">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-neutral-500">{m.category}</div>
        <h3 className="mt-2 text-2xl font-medium tracking-tight text-neutral-950">{m.name}</h3>
        <p className="mt-3 text-sm leading-relaxed text-neutral-600 line-clamp-2">{m.description}</p>

        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs text-neutral-600">
          {m.range_km && (
            <span className="inline-flex items-center gap-1.5">
              <BatteryCharging size={13} /> bis {m.range_km} km
            </span>
          )}
          {m.power && (
            <span className="inline-flex items-center gap-1.5">
              <Gauge size={13} /> {m.power}
            </span>
          )}
          {m.seats && (
            <span className="inline-flex items-center gap-1.5">
              <Users size={13} /> {m.seats} Sitze
            </span>
          )}
        </div>

        <div className="mt-5 pt-5 border-t border-neutral-100 flex-1">
          <div className="text-xl font-semibold tracking-tight text-neutral-950" data-testid={`model-price-${m.id}`}>
            {m.price_from ? `ab ${formatCHF(m.price_from)}` : "Preis auf Anfrage"}
          </div>
          <p className="mt-1.5 text-[10px] leading-relaxed text-neutral-400">
            Unverbindlicher Listenpreis gemäss KIA Schweiz. Preisänderungen vorbehalten.
          </p>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-2">
          <button
            data-testid={`model-details-${m.id}`}
            onClick={() => onShowDetail(m)}
            className="inline-flex items-center justify-center gap-1.5 border border-neutral-950 text-neutral-950 text-xs font-semibold px-4 py-3 transition-colors hover:bg-neutral-950 hover:text-white"
          >
            Details ansehen <ArrowUpRight size={13} />
          </button>
          <button
            data-testid={`model-offerte-${m.id}`}
            onClick={() => requestOfferte(m.name)}
            className="bg-neutral-950 text-white text-xs font-semibold px-4 py-3 transition-colors hover:bg-neutral-800"
          >
            Firmenofferte
          </button>
        </div>
      </div>
    </motion.article>
  );
}

export default function Models({ models, onShowDetail }) {
  const [filter, setFilter] = useState("alle");
  const filtered = useMemo(
    () =>
      models
        .filter((m) => matches(m, filter))
        .sort((a, b) => (a.price_from ?? Infinity) - (b.price_from ?? Infinity)),
    [models, filter]
  );

  return (
    <section id="modelle" data-testid="models-section" className="py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeading
          number="03"
          label="Modellpalette"
          title="Die komplette KIA Modellpalette für Ihr Unternehmen"
          sub="Alle aktuell in der Schweiz erhältlichen KIA Modelle – vom effizienten Kleinwagen bis zum elektrischen Nutzfahrzeug."
        />

        <div data-testid="model-filters" className="flex flex-wrap gap-2 mb-12">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`filter-${f.key.toLowerCase().replace(/[^a-z]/g, "-")}`}
              onClick={() => setFilter(f.key)}
              className={`text-xs font-semibold uppercase tracking-[0.12em] px-5 py-2.5 border transition-colors duration-300 ${
                filter === f.key
                  ? "bg-neutral-950 text-white border-neutral-950"
                  : "bg-white text-neutral-600 border-neutral-300 hover:border-neutral-950 hover:text-neutral-950"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence mode="popLayout">
            {filtered.map((m) => (
              <ModelCard key={m.id} m={m} onShowDetail={onShowDetail} />
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
